import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

const STATES = [
  ["MH","Maharashtra"], ["TN","Tamil Nadu"], ["KA","Karnataka"],
  ["TS","Telangana"], ["AP","Andhra Pradesh"], ["WB","West Bengal"],
  ["KL","Kerala"], ["DL","Delhi"], ["GJ","Gujarat"], ["RJ","Rajasthan"],
  ["UP","Uttar Pradesh"], ["PB","Punjab"], ["HR","Haryana"], ["GA","Goa"],
  ["MP","Madhya Pradesh"], ["OR","Odisha"], ["AS","Assam"],
];

export default function ProfileEdit() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    bio: "", home_city: "", home_state: "", photo_url: "",
    skills: [], languages: [], instagram: "", showreel_url: "",
    past_credits: [],
  });
  const [skillsInput, setSkillsInput] = useState("");
  const [langsInput, setLangsInput] = useState("");

  useEffect(() => {
    if (!user) return;
    api.get(`/profile/${user.id}`).then(({ data }) => {
      setForm({
        bio: data.bio || "",
        home_city: data.home_city || "",
        home_state: data.home_state || "",
        photo_url: data.photo_url || "",
        skills: data.skills || [],
        languages: data.languages || [],
        instagram: data.instagram || "",
        showreel_url: data.showreel_url || "",
        past_credits: data.past_credits || [],
      });
      setSkillsInput((data.skills || []).join(", "));
      setLangsInput((data.languages || []).join(", "));
    });
  }, [user]);

  const addCredit = () => setForm({
    ...form,
    past_credits: [...form.past_credits, { title: "", role: user?.primary_role || "", year: new Date().getFullYear(), project_type: "feature", notes: "" }],
  });

  const setCredit = (i, k, v) => setForm({
    ...form,
    past_credits: form.past_credits.map((c, idx) => idx === i ? { ...c, [k]: v } : c),
  });

  const removeCredit = (i) => setForm({
    ...form,
    past_credits: form.past_credits.filter((_, idx) => idx !== i),
  });

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      skills: skillsInput.split(",").map(s => s.trim()).filter(Boolean),
      languages: langsInput.split(",").map(s => s.trim()).filter(Boolean),
    };
    try {
      await api.put("/profile/me", payload);
      toast.success("Profile saved");
      await refresh();
      nav(`/profile/${user.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!user) return null;

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-10" data-testid="profile-edit">
      <Link to={`/profile/${user.id}`} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/60 hover:text-white">
        <ArrowLeft size={14} /> Back to my profile
      </Link>
      <div className="mt-4 text-[10px] tracking-[0.3em] uppercase text-white/50">Edit</div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2">My Public Profile</h1>
      <p className="mt-3 text-sm text-white/55">What you write here is what producers see when they discover you.</p>

      <form onSubmit={save} className="mt-8 space-y-6">
        <div className="cs-card rounded-2xl p-6 space-y-4">
          <div className="font-display text-xl uppercase tracking-tight">About</div>
          <Field label="Bio · short pitch">
            <Textarea value={form.bio} data-testid="pf-bio" maxLength={500}
              placeholder="One paragraph. Who you are, what you do, what you're known for."
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              className="bg-black/40 border-white/15 min-h-[110px]" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Home City">
              <Input value={form.home_city} data-testid="pf-city"
                onChange={(e) => setForm({ ...form, home_city: e.target.value })}
                className="bg-black/40 border-white/15" />
            </Field>
            <Field label="Home State">
              <Select value={form.home_state || "none"} onValueChange={(v) => setForm({ ...form, home_state: v === "none" ? "" : v })}>
                <SelectTrigger className="bg-black/40 border-white/15" data-testid="pf-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="none">—</SelectItem>
                  {STATES.map(([k,l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Profile Photo URL (optional)">
              <Input value={form.photo_url} data-testid="pf-photo"
                placeholder="https://…"
                onChange={(e) => setForm({ ...form, photo_url: e.target.value })}
                className="bg-black/40 border-white/15" />
            </Field>
            <Field label="Showreel URL">
              <Input value={form.showreel_url} data-testid="pf-showreel"
                placeholder="Vimeo / YouTube link"
                onChange={(e) => setForm({ ...form, showreel_url: e.target.value })}
                className="bg-black/40 border-white/15" />
            </Field>
            <Field label="Instagram">
              <Input value={form.instagram} data-testid="pf-instagram"
                placeholder="@handle"
                onChange={(e) => setForm({ ...form, instagram: e.target.value })}
                className="bg-black/40 border-white/15" />
            </Field>
          </div>
        </div>

        <div className="cs-card rounded-2xl p-6 space-y-4">
          <div className="font-display text-xl uppercase tracking-tight">Skills & Languages</div>
          <Field label="Skills (comma-separated)">
            <Input value={skillsInput} data-testid="pf-skills"
              placeholder="Crew Management, Drone Camera, Action Choreography"
              onChange={(e) => setSkillsInput(e.target.value)}
              className="bg-black/40 border-white/15" />
          </Field>
          <Field label="Languages spoken (comma-separated)">
            <Input value={langsInput} data-testid="pf-langs"
              placeholder="Hindi, English, Marathi"
              onChange={(e) => setLangsInput(e.target.value)}
              className="bg-black/40 border-white/15" />
          </Field>
        </div>

        <div className="cs-card rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="font-display text-xl uppercase tracking-tight">Past Credits</div>
            <Button type="button" onClick={addCredit} variant="outline"
              className="bg-transparent border-white/20 text-white hover:bg-white hover:text-black h-9 px-3 text-xs uppercase tracking-[0.2em]"
              data-testid="pf-add-credit">
              <Plus size={14} className="mr-1" /> Add
            </Button>
          </div>
          {form.past_credits.length === 0 && (
            <div className="text-xs text-white/40 text-center py-4">No credits yet. Click Add to log your past work.</div>
          )}
          {form.past_credits.map((c, i) => (
            <div key={`${c.title || "credit"}-${c.year || ""}-${i}`} className="border border-white/10 rounded-lg p-4 space-y-2.5" data-testid={`pf-credit-${i}`}>
              <div className="grid grid-cols-12 gap-2">
                <div className="col-span-5">
                  <Input value={c.title} placeholder="Project title"
                    onChange={(e) => setCredit(i, "title", e.target.value)}
                    className="bg-black/40 border-white/15 h-9" />
                </div>
                <div className="col-span-3">
                  <Input value={c.role} placeholder="Your role"
                    onChange={(e) => setCredit(i, "role", e.target.value)}
                    className="bg-black/40 border-white/15 h-9" />
                </div>
                <div className="col-span-2">
                  <Input value={c.year || ""} type="number" placeholder="Year"
                    onChange={(e) => setCredit(i, "year", parseInt(e.target.value) || null)}
                    className="bg-black/40 border-white/15 h-9" />
                </div>
                <div className="col-span-1">
                  <Select value={c.project_type || "feature"} onValueChange={(v) => setCredit(i, "project_type", v)}>
                    <SelectTrigger className="bg-black/40 border-white/15 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="feature">Feature</SelectItem>
                      <SelectItem value="ad_film">Ad Film</SelectItem>
                      <SelectItem value="tvc">TVC</SelectItem>
                      <SelectItem value="short">Short</SelectItem>
                      <SelectItem value="series">Series</SelectItem>
                      <SelectItem value="music_video">Music Video</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <button type="button" onClick={() => removeCredit(i)} className="col-span-1 text-white/40 hover:text-red-400">
                  <Trash2 size={14} />
                </button>
              </div>
              <Input value={c.notes || ""} placeholder="Notes (optional)"
                onChange={(e) => setCredit(i, "notes", e.target.value)}
                className="bg-black/40 border-white/15 h-9 text-xs" />
            </div>
          ))}
        </div>

        <Button type="submit" className="btn-themed w-full h-12 rounded-full uppercase tracking-[0.25em] text-xs"
                data-testid="pf-save">
          <Save size={14} className="mr-2" /> Save profile
        </Button>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] tracking-[0.25em] uppercase text-white/60">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
